<template>
    <transition name="slide-fade">
        <div
            v-if="systemAlerts.length > 0"
            class="system-alerts-container"
            :style="{ height: `${(showingAll ? systemAlerts.length : 1) * $systemAlertHeight}px` }"
        >
            <transition-group name="slide-fade">
                <system-alert
                    v-for="(alert, index) in systemAlerts"
                    v-show="showingAll || index === 0"
                    :key="alert.id"
                    :alert="alert"
                    :primary="index === 0"
                    :total-count="systemAlerts.length"
                    :showing-all="showingAll"
                    :dismiss-error="dismissErrors[alert.uuid] || null"
                    @dismissed="dismiss"
                    @toggle="toggle"
                />
            </transition-group>
        </div>
    </transition>
</template>

<script>
import { mapState } from 'vuex';

import SystemAlert from '@/components/SystemAlert.vue';
import { SYSTEM_ALERT_EVENTS } from '@/constants/systemAlerts.js';
import { initialState } from '@/Helpers.js';
import SystemAlertModel from '@/models/SystemAlert.js';
import axios from '@/plugins/axios.js';

export const SYSTEM_ALERTS_VISIBLE_URL = '/api/system-alerts/visible';

/**
 * Freshness rules (2026-10-01 amendment; 60 s cadence per Jonathan, 2026-10-15):
 * - The foreground refresh runs every 60 s.
 * - A snapshot is fresh for 60 s from the moment its successful RESPONSE arrived.
 * - When the deadline passes while a refresh attempt (a request or its one quick retry) is still
 *   pending, that attempt may finish within FRESHNESS_GRACE_MS; otherwise banners hide at the
 *   deadline. So stale content is never shown later than 65 s after the last successful response.
 */
export const FRESHNESS_DEADLINE_MS = 60 * 1000;

export const REFRESH_INTERVAL_MS = 60 * 1000;

/** A realtime event schedules a jittered reconciliation so one publish does not stampede the API. */
export const EVENT_RECONCILE_MIN_MS = 2 * 1000;
export const EVENT_RECONCILE_MAX_MS = 15 * 1000;

/** Focus, visibility, online and pageshow often fire together; they share one refresh. */
export const RESUME_DEBOUNCE_MS = 250;

/** A hung request fails, and suppresses, within the grace window. */
export const REQUEST_TIMEOUT_MS = 5 * 1000;

/** How long past the deadline a pending refresh attempt may keep the banner up. */
export const FRESHNESS_GRACE_MS = REQUEST_TIMEOUT_MS;

/**
 * A heartbeat notices a suspension or clock jump that fired no focus/visibility event (the
 * deadline timers themselves may be paused across sleep).
 */
export const HEARTBEAT_MS = 2 * 1000;
const HEARTBEAT_JUMP_MS = 5 * 1000;

const CHANNEL_PREFIX = 'system-alerts.';

const MAX_TIMER_DELAY_MS = 2147483647;

/** One quick retry after a transient failure (a deploy or load-balancer blip), jittered. */
export const FAILURE_RETRY_MIN_MS = 3 * 1000;
export const FAILURE_RETRY_MAX_MS = 8 * 1000;

/** The quick retry is pulled in so it still lands inside the grace window. */
const FAILURE_RETRY_DEADLINE_MARGIN_MS = 500;

/**
 * Freshness is measured on two clocks. The monotonic clock survives the wall clock being set
 * backwards; the wall clock keeps running while the machine sleeps (performance.now() pauses
 * there on some platforms). A snapshot is stale once either clock says it is too old.
 *
 * @returns {{wall: number, mono: number}}
 */
function freshnessStamp() {
    return { wall: Date.now(), mono: performance.now() };
}

/**
 * @param {{wall: number, mono: number}|null} stamp
 * @returns {number} milliseconds of freshness left (zero or less means stale)
 */
function freshnessRemaining(stamp) {
    if (stamp === null) {
        return 0;
    }

    const age = Math.max(performance.now() - stamp.mono, Date.now() - stamp.wall);

    return FRESHNESS_DEADLINE_MS - age;
}

/**
 * Banner state that must outlive a remount (AppLayout unmounts for login and password pages):
 * the page-load snapshot is consumed once, and an in-session dismissal stays dismissed.
 */
const session = {
    userId: null,
    bootstrapped: false,
    lastSuccessAt: null,
    channels: [],
    dismissedUuids: new Set(),
};

/** How many mounted banners hold each private channel, so one never leaves another's channel. */
const channelHolders = new Map();

/** Test seam: forget the per-page session and channel holders. */
export function resetSystemAlertsSession() {
    session.userId = null;
    session.bootstrapped = false;
    session.lastSuccessAt = null;
    session.channels = [];
    session.dismissedUuids = new Set();
    channelHolders.clear();
}

/**
 * Parse the frozen UTC timestamps ("2026-10-15T16:00:00Z"), tolerating Laravel's
 * microsecond form and a missing zone designator (stored values are UTC).
 *
 * @param {unknown} value
 * @returns {number|null}
 */
export function parseAlertTimestamp(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }

    let normalized = String(value)
        .trim()
        .replace(/^(\d{4}-\d{2}-\d{2}) (\d)/, '$1T$2')
        .replace(/(\.\d{3})\d+/, '$1');

    if (/T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(normalized)) {
        normalized = `${normalized}Z`;
    }

    const parsed = Date.parse(normalized);

    return Number.isNaN(parsed) ? null : parsed;
}

/**
 * @param {{starts_at?: unknown, expires_at?: unknown}} alert
 * @param {number} now
 * @returns {boolean}
 */
export function isAlertLive(alert, now) {
    const startsAt = parseAlertTimestamp(alert.starts_at);
    const expiresAt = parseAlertTimestamp(alert.expires_at);

    return (startsAt === null || startsAt <= now) && (expiresAt === null || expiresAt > now);
}

function isDisplayableAlert(alert) {
    return !!alert && typeof alert === 'object' && alert.id !== null && alert.id !== undefined && !!alert.uuid;
}

function channelNames(channels) {
    if (!Array.isArray(channels)) {
        return [];
    }

    return [...new Set(channels.filter(name => typeof name === 'string' && name.startsWith(CHANNEL_PREFIX)))];
}

function statusOf(error) {
    return error?.response?.status ?? null;
}

export default {
    name: 'SystemAlerts',

    components: { SystemAlert },

    emits: ['toggled', 'alerts-changed'],

    data: () => ({
        showingAll: false,
        now: Date.now(),
        suppressed: false,
        pendingDismissals: {},
        dismissErrors: {},
        desiredChannels: [],
    }),

    computed: {
        ...mapState('Auth', { user: state => state.user }),

        userId() {
            return this.user?.id ?? null;
        },

        echoReady() {
            return !!this.$store.state.Online.$echo;
        },

        storedAlerts() {
            return SystemAlertModel.query().get();
        },

        systemAlerts() {
            if (this.suppressed) {
                return [];
            }

            return this.storedAlerts.filter(
                alert => !this.pendingDismissals[alert.uuid] && isAlertLive(alert, this.now)
            );
        },
    },

    watch: {
        echoReady: {
            // Immediate: a remount after Echo is already connected must still subscribe.
            immediate: true,
            handler(ready) {
                if (ready) {
                    this.bindConnection();
                    this.syncSubscriptions();
                }
            },
        },

        userId(current, previous) {
            if (current !== previous) {
                this.switchUser(current);
            }
        },

        storedAlerts() {
            this.scheduleBoundaryTick();
        },

        systemAlerts: {
            handler(alerts) {
                // Pass the count so parents can clear layout offset immediately when
                // the last alert is dismissed (leave transitions can leave a ghost DOM node).
                this.$nextTick(() => {
                    this.$emit('alerts-changed', alerts.length);
                });
            },
            deep: true,
        },
    },

    beforeCreate() {
        // Non-reactive bookkeeping; set before the immediate echoReady watcher runs.
        this.isActive = true;
        this.halted = false;
        this.subscriptions = new Map();
        this.inFlight = null;
        this.followUpQueued = false;
        this.requestGeneration = 0;
        this.stateVersion = 0;
        this.boundaryTimer = null;
        this.reconcileTimer = null;
        this.resumeTimer = null;
        this.intervalTimer = null;
        this.deadlineTimer = null;
        this.graceTimer = null;
        this.heartbeatTimer = null;
        this.lastBeat = null;
        this.failureRetryTimer = null;
        this.failureRetryUsed = false;
        this.boundConnection = null;
        this.sawConnectionLoss = false;
    },

    created() {
        this.adoptSession(this.userId);
        this.restoreSessionState();
    },

    mounted() {
        window.addEventListener('focus', this.onResume);
        window.addEventListener('online', this.onResume);
        window.addEventListener('pageshow', this.onResume);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        this.intervalTimer = setInterval(this.onIntervalTick, REFRESH_INTERVAL_MS);
        this.lastBeat = freshnessStamp();
        this.heartbeatTimer = setInterval(this.onHeartbeat, HEARTBEAT_MS);
        this.scheduleBoundaryTick();
        this.refresh();
    },

    beforeUnmount() {
        this.isActive = false;
        this.requestGeneration += 1;

        window.removeEventListener('focus', this.onResume);
        window.removeEventListener('online', this.onResume);
        window.removeEventListener('pageshow', this.onResume);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);

        clearInterval(this.intervalTimer);
        clearInterval(this.heartbeatTimer);
        clearTimeout(this.boundaryTimer);
        clearTimeout(this.reconcileTimer);
        clearTimeout(this.resumeTimer);
        clearTimeout(this.deadlineTimer);
        clearTimeout(this.graceTimer);
        this.cancelFailureRetry();

        this.unbindConnection();
        this.unsubscribeAll();
    },

    methods: {
        /**
         * The page-load LoadingState is a bootstrap source only; the mounted refresh replaces it.
         */
        bootstrapFromPageLoad() {
            const pageUserId = initialState('AuthenticatedUser.id', null);

            if (pageUserId !== null && pageUserId !== this.userId) {
                return;
            }

            const alerts = (initialState('SystemAlerts', []) || []).filter(
                alert => isDisplayableAlert(alert) && !session.dismissedUuids.has(alert.uuid)
            );

            SystemAlertModel.create({ data: alerts });

            session.lastSuccessAt = freshnessStamp();
            session.channels = channelNames(initialState('SystemAlertChannels', []));
            this.armDeadline();
        },

        adoptSession(userId) {
            if (session.userId !== null && session.userId !== userId) {
                // Another user's alerts, dismissals and channels never carry over.
                session.bootstrapped = true;
                session.lastSuccessAt = null;
                session.channels = [];
                session.dismissedUuids = new Set();
                SystemAlertModel.deleteAll();
            }

            session.userId = userId;
        },

        switchUser(userId) {
            this.cancelFailureRetry();
            this.failureRetryUsed = false;
            this.requestGeneration += 1;
            this.stateVersion += 1;
            this.pendingDismissals = {};
            this.dismissErrors = {};
            this.halted = false;
            this.adoptSession(userId);
            this.restoreSessionState();

            if (userId !== null) {
                this.refresh({ followUp: true });
            }
        },

        /**
         * Bootstrap from the page load once the user is known, resubscribe at once to the
         * server's last channel list (the refresh corrects it), and hide anything already
         * past the freshness deadline (e.g. a remount after the tab was suspended).
         */
        restoreSessionState() {
            if (this.userId !== null && !session.bootstrapped) {
                session.bootstrapped = true;
                this.bootstrapFromPageLoad();
            }

            this.desiredChannels = [...session.channels];
            this.syncSubscriptions();
            this.suppressed = this.isStale();
            this.armDeadline();
        },

        isStale() {
            return freshnessRemaining(session.lastSuccessAt) <= 0;
        },

        /** A request in flight or the quick retry waiting to run. */
        hasPendingAttempt() {
            return this.inFlight !== null || this.failureRetryTimer !== null;
        },

        /**
         * Hide banners once the snapshot is past the deadline and nothing is pending, and in any
         * case once the grace window is over. Only a successful snapshot shows them again.
         */
        evaluateFreshness() {
            this.now = Date.now();

            const remaining = freshnessRemaining(session.lastSuccessAt);

            if (remaining <= -FRESHNESS_GRACE_MS || (remaining <= 0 && !this.hasPendingAttempt())) {
                this.suppressed = true;
            }
        },

        /**
         * Fetch the authoritative visible set. Overlapping triggers share one request; a trigger
         * that must see newer server state queues exactly one follow-up.
         *
         * @param {{followUp?: boolean}} options
         * @returns {Promise<void>}
         */
        refresh({ followUp = false } = {}) {
            if (!this.isActive || this.halted || this.userId === null) {
                return Promise.resolve();
            }

            if (this.inFlight) {
                this.followUpQueued = this.followUpQueued || followUp;

                return this.inFlight;
            }

            const request = {
                generation: (this.requestGeneration += 1),
                userId: this.userId,
                stateVersion: this.stateVersion,
            };

            const inFlight = axios
                // retry: false opts out of the global network-error retries (up to ~87 s), which
                // would otherwise hold a failed refresh, and stale banners, past the deadline.
                .get(SYSTEM_ALERTS_VISIBLE_URL, { errorHandle: false, retry: false, timeout: REQUEST_TIMEOUT_MS })
                .then(
                    response => this.applySnapshot(response, request),
                    error => this.handleRefreshFailure(error, request)
                )
                .finally(() => {
                    if (this.inFlight === inFlight) {
                        this.inFlight = null;
                    }

                    if (this.followUpQueued && this.isActive) {
                        this.followUpQueued = false;
                        this.refresh();
                    }

                    if (this.isActive) {
                        // A settled attempt no longer holds the grace window open.
                        this.evaluateFreshness();
                    }
                });

            this.inFlight = inFlight;

            return inFlight;
        },

        isCurrentRequest(request) {
            return this.isActive && request.generation === this.requestGeneration && request.userId === this.userId;
        },

        applySnapshot(response, request) {
            if (!this.isCurrentRequest(request)) {
                return;
            }

            if (request.stateVersion !== this.stateVersion) {
                // A realtime change or dismissal landed while this was in flight; the response may
                // predate it, so ask again rather than resurrect or overwrite newer state.
                this.followUpQueued = true;

                return;
            }

            const payload = response?.data;

            if (!Array.isArray(payload?.data) || !Array.isArray(payload?.channels)) {
                this.handleRefreshFailure(new Error('Malformed system alerts snapshot'), request);

                return;
            }

            const alerts = payload.data.filter(
                alert => isDisplayableAlert(alert) && !session.dismissedUuids.has(alert.uuid)
            );

            SystemAlertModel.create({ data: alerts });

            const visibleUuids = new Set(alerts.map(alert => alert.uuid));

            this.dismissErrors = Object.fromEntries(
                Object.entries(this.dismissErrors).filter(([uuid]) => visibleUuids.has(uuid))
            );

            // Freshness runs from the moment this response arrived.
            session.lastSuccessAt = freshnessStamp();

            this.failureRetryUsed = false;
            this.cancelFailureRetry();
            this.suppressed = false;
            this.armDeadline();
            this.now = Date.now();
            session.channels = channelNames(payload.channels);
            this.desiredChannels = [...session.channels];
            this.syncSubscriptions();
        },

        handleRefreshFailure(error, request) {
            if (!this.isCurrentRequest(request)) {
                return;
            }

            const status = statusOf(error);

            if (status === 401 || status === 403) {
                this.clearForbidden(status);

                return;
            }

            this.scheduleFailureRetry();
            this.evaluateFreshness();
        },

        /**
         * After a failure, retry once with 3–8 s jitter, capped so the retry starts inside the
         * grace window; no retry once that window has closed. The request itself has no transport
         * retries and a 5 s timeout, so a persistent failure still hides banners on time.
         */
        scheduleFailureRetry() {
            if (this.failureRetryUsed || this.failureRetryTimer || !this.isActive) {
                return;
            }

            const jitter = FAILURE_RETRY_MIN_MS + Math.random() * (FAILURE_RETRY_MAX_MS - FAILURE_RETRY_MIN_MS);
            const beforeDeadline =
                freshnessRemaining(session.lastSuccessAt) + FRESHNESS_GRACE_MS - FAILURE_RETRY_DEADLINE_MARGIN_MS;

            if (beforeDeadline <= 0) {
                return;
            }

            this.failureRetryUsed = true;
            this.failureRetryTimer = setTimeout(
                () => {
                    this.failureRetryTimer = null;
                    this.refresh({ followUp: true });
                },
                Math.max(0, Math.min(jitter, beforeDeadline))
            );
        },

        cancelFailureRetry() {
            clearTimeout(this.failureRetryTimer);
            this.failureRetryTimer = null;
        },

        /**
         * The server no longer recognises this session or user: show nothing and hold no channels.
         *
         * @param {number} status
         */
        clearForbidden(status) {
            this.cancelFailureRetry();
            this.failureRetryUsed = false;
            this.requestGeneration += 1;
            this.stateVersion += 1;
            session.lastSuccessAt = null;
            this.armDeadline();
            session.channels = [];
            SystemAlertModel.deleteAll();
            this.pendingDismissals = {};
            this.dismissErrors = {};
            this.desiredChannels = [];
            this.syncSubscriptions();

            if (status === 401) {
                // The session is gone; the login flow remounts the layout with a fresh session.
                this.halted = true;
            }
        },

        /**
         * At the deadline: if no refresh is pending (the tick ran late, or the last success was
         * off-cycle), start one now in the foreground so it can use the grace window; then hide
         * if nothing is pending. The grace timer closes the window regardless.
         */
        onDeadline() {
            if (!this.hasPendingAttempt() && document.visibilityState !== 'hidden') {
                this.refresh();
            }

            this.evaluateFreshness();
        },

        armDeadline() {
            clearTimeout(this.deadlineTimer);
            clearTimeout(this.graceTimer);
            this.deadlineTimer = null;
            this.graceTimer = null;

            if (!this.isActive || session.lastSuccessAt === null) {
                return;
            }

            const remaining = Math.max(0, freshnessRemaining(session.lastSuccessAt));

            this.deadlineTimer = setTimeout(() => {
                this.deadlineTimer = null;
                this.onDeadline();
            }, remaining);

            // Hard 65 s bound: when the grace window closes, hide without re-reading the clocks
            // (a timer can fire a fraction of a millisecond before the boundary it was armed
            // for). Any newer success replaces the stamp and re-arms this timer.
            const armedFor = session.lastSuccessAt;

            this.graceTimer = setTimeout(() => {
                this.graceTimer = null;

                if (session.lastSuccessAt !== armedFor) {
                    return;
                }

                this.now = Date.now();
                this.suppressed = true;
            }, remaining + FRESHNESS_GRACE_MS);
        },

        onIntervalTick() {
            if (document.visibilityState !== 'hidden') {
                this.refresh();
            }

            // Timers also resume after a suspension that fired no focus or visibility event.
            this.evaluateFreshness();
        },

        /**
         * A heartbeat far later than scheduled, or wall and monotonic clocks drifting apart,
         * means the machine slept or the clock jumped: treat it like returning to the tab.
         */
        onHeartbeat() {
            const beat = freshnessStamp();
            const previous = this.lastBeat ?? beat;
            const wallDelta = beat.wall - previous.wall;
            const monoDelta = beat.mono - previous.mono;

            this.lastBeat = beat;

            if (wallDelta - HEARTBEAT_MS <= HEARTBEAT_JUMP_MS && Math.abs(wallDelta - monoDelta) <= HEARTBEAT_JUMP_MS) {
                return;
            }

            if (document.visibilityState === 'hidden') {
                this.evaluateFreshness();

                return;
            }

            this.onResume();
        },

        onVisibilityChange() {
            if (document.visibilityState !== 'hidden') {
                this.onResume();
            }
        },

        /**
         * Returning to the tab: hide expired and stale content now, then reconcile.
         */
        onResume() {
            this.evaluateFreshness();

            clearTimeout(this.resumeTimer);
            this.resumeTimer = setTimeout(() => {
                this.resumeTimer = null;
                this.refresh({ followUp: true });
            }, RESUME_DEBOUNCE_MS);
        },

        scheduleEventReconcile() {
            if (this.reconcileTimer) {
                return;
            }

            const delay = EVENT_RECONCILE_MIN_MS + Math.random() * (EVENT_RECONCILE_MAX_MS - EVENT_RECONCILE_MIN_MS);

            this.reconcileTimer = setTimeout(() => {
                this.reconcileTimer = null;
                this.refresh({ followUp: true });
            }, delay);
        },

        /**
         * Wake exactly when the next alert starts or expires, so expiry needs no reload.
         */
        scheduleBoundaryTick() {
            clearTimeout(this.boundaryTimer);
            this.boundaryTimer = null;

            if (!this.isActive) {
                return;
            }

            const now = Date.now();
            const boundaries = this.storedAlerts
                .flatMap(alert => [parseAlertTimestamp(alert.starts_at), parseAlertTimestamp(alert.expires_at)])
                .filter(timestamp => timestamp !== null && timestamp > now);

            if (boundaries.length === 0) {
                return;
            }

            const delay = Math.min(Math.min(...boundaries) - now, MAX_TIMER_DELAY_MS);

            this.boundaryTimer = setTimeout(() => {
                this.boundaryTimer = null;
                this.now = Date.now();
                this.scheduleBoundaryTick();
            }, delay);
        },

        echo() {
            return this.$echo || this.$store.state.Online.$echo || null;
        },

        bindConnection() {
            const connection = this.echo()?.connector?.pusher?.connection;

            if (this.boundConnection || !connection?.bind) {
                return;
            }

            connection.bind('state_change', this.onConnectionStateChange);
            this.boundConnection = connection;
        },

        unbindConnection() {
            this.boundConnection?.unbind?.('state_change', this.onConnectionStateChange);
            this.boundConnection = null;
        },

        onConnectionStateChange({ previous, current } = {}) {
            if (current !== 'connected') {
                if (previous === 'connected' || ['unavailable', 'failed', 'disconnected'].includes(current)) {
                    this.sawConnectionLoss = true;
                }

                return;
            }

            if (this.sawConnectionLoss) {
                // Broadcasts sent while disconnected are gone; the snapshot recovers them.
                this.sawConnectionLoss = false;
                this.refresh({ followUp: true });
            }
        },

        /**
         * Hold exactly the private channels the server listed: subscribe to new ones and release
         * the ones it dropped. The client never decides membership itself.
         */
        syncSubscriptions() {
            const desired = new Set(this.isActive ? this.desiredChannels : []);

            [...this.subscriptions.keys()]
                .filter(name => !desired.has(name))
                .forEach(name => {
                    this.unsubscribe(name);
                });

            if (!this.echoReady || !this.echo()) {
                return;
            }

            desired.forEach(name => {
                if (!this.subscriptions.has(name)) {
                    this.subscribe(name);
                }
            });
        },

        subscribe(name) {
            const echo = this.echo();
            const channel = echo.private(name);
            const handlers = {
                [SYSTEM_ALERT_EVENTS.published]: payload => this.onAlertPublished(payload),
                [SYSTEM_ALERT_EVENTS.updated]: payload => this.onAlertUpdated(payload),
                [SYSTEM_ALERT_EVENTS.removed]: payload => this.onAlertRemoved(payload),
            };

            Object.entries(handlers).forEach(([event, handler]) => {
                channel.listen(event, handler);
            });

            this.subscriptions.set(name, { echo, channel, handlers });
            channelHolders.set(name, (channelHolders.get(name) ?? 0) + 1);
        },

        unsubscribe(name) {
            const subscription = this.subscriptions.get(name);

            if (!subscription) {
                return;
            }

            this.subscriptions.delete(name);

            const { echo, channel, handlers } = subscription;

            Object.entries(handlers).forEach(([event, handler]) => {
                channel.stopListening?.(event, handler);
            });

            const remaining = (channelHolders.get(name) ?? 1) - 1;

            if (remaining > 0) {
                channelHolders.set(name, remaining);

                return;
            }

            channelHolders.delete(name);

            if (typeof echo.leaveChannel === 'function') {
                echo.leaveChannel(`private-${name}`);
            } else {
                echo.leave?.(name);
            }
        },

        unsubscribeAll() {
            [...this.subscriptions.keys()].forEach(name => {
                this.unsubscribe(name);
            });
        },

        receivedAlert(payload) {
            const alert = payload?.systemAlert;

            if (!this.isActive || !isDisplayableAlert(alert) || session.dismissedUuids.has(alert.uuid)) {
                return null;
            }

            return alert;
        },

        onAlertPublished(payload) {
            const alert = this.receivedAlert(payload);

            if (!alert) {
                return;
            }

            this.stateVersion += 1;
            SystemAlertModel.insert({ data: alert });
            this.now = Date.now();
            this.scheduleEventReconcile();
        },

        /**
         * Updates fan out to every current recipient, including people who dismissed the alert,
         * so an update only edits an alert this banner already holds. An unknown uuid asks the
         * snapshot instead of showing a record the server may be hiding from this user.
         */
        onAlertUpdated(payload) {
            const alert = this.receivedAlert(payload);

            if (!alert) {
                return;
            }

            this.stateVersion += 1;

            if (SystemAlertModel.query().where('uuid', alert.uuid).exists()) {
                SystemAlertModel.insert({ data: alert });
                this.now = Date.now();
            }

            this.scheduleEventReconcile();
        },

        onAlertRemoved(payload) {
            const uuid = payload?.uuid;

            if (!this.isActive || !uuid) {
                return;
            }

            this.stateVersion += 1;
            SystemAlertModel.delete(alert => alert.uuid === uuid);

            if (this.dismissErrors[uuid]) {
                const { [uuid]: _removed, ...rest } = this.dismissErrors;
                this.dismissErrors = rest;
            }

            this.scheduleEventReconcile();
        },

        /**
         * Hide the alert while the request runs, keep it hidden only once the server confirms,
         * and otherwise put it back with an inline error (the app suppresses many snackbars).
         *
         * @param {{uuid: string}} alert
         * @returns {Promise<void>}
         */
        async dismiss(alert) {
            const uuid = alert?.uuid;

            if (!uuid || this.pendingDismissals[uuid]) {
                return;
            }

            const { [uuid]: _previousError, ...otherErrors } = this.dismissErrors;
            this.dismissErrors = otherErrors;
            this.pendingDismissals = { ...this.pendingDismissals, [uuid]: true };

            const { userId } = this;

            try {
                await axios.delete(`/api/system-alerts/${encodeURIComponent(uuid)}/dismiss`, { errorHandle: false });

                if (userId === this.userId) {
                    session.dismissedUuids.add(uuid);
                    this.stateVersion += 1;
                    SystemAlertModel.delete(record => record.uuid === uuid);
                }
            } catch (error) {
                if (this.isActive && userId === this.userId) {
                    this.handleDismissFailure(uuid, statusOf(error));
                }
            } finally {
                const { [uuid]: _settled, ...stillPending } = this.pendingDismissals;
                this.pendingDismissals = stillPending;
            }
        },

        handleDismissFailure(uuid, status) {
            if (status === 401) {
                this.clearForbidden(status);

                return;
            }

            if (status === 403) {
                // The server says this user cannot see the alert any more.
                this.stateVersion += 1;
                SystemAlertModel.delete(record => record.uuid === uuid);
                this.refresh({ followUp: true });

                return;
            }

            // Restored only if the current snapshot still holds it; freshness rules still apply.
            this.dismissErrors = {
                ...this.dismissErrors,
                [uuid]: status === 400 ? 'This alert can’t be dismissed.' : 'Couldn’t dismiss. Try again.',
            };
        },

        toggle() {
            this.showingAll = !this.showingAll;

            this.$emit('toggled', this.showingAll);
            this.$nextTick(() => {
                this.$emit('alerts-changed', this.systemAlerts.length);
            });
        },
    },
};
</script>

<style lang="scss">
@import '~assets/scss/toolbox.scss';

.system-alerts-container {
    width: 100%;
    overflow: hidden;
    display: block;
    visibility: visible;

    // Alert is in normal flow within fixed header
    // Need higher z-index than navigation (z-index: 12) to receive clicks
    // Navigation is positioned below alert via top: calc(var(--app-header-alert-height) + 1px)
    // Apply z-index fix for both mobile and desktop to ensure clicks work
    position: relative;
    z-index: 20; // Higher than navigation's z-index: 12 to ensure clicks work
}

//.slide-fade-enter-active {
//    transition: all 0.3s ease-out;
//}
//
//.slide-fade-leave-active {
//    transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
//}
//
//.slide-fade-enter-from,
//.slide-fade-leave-to {
//    transform: translateY(-44px);
//    opacity: 0;
//}
</style>
